import React, { useEffect, useState, lazy, Suspense } from "react";
import { Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Toaster } from "react-hot-toast";

import Navbar from "./components/layouts/Navbar";
import Footer from "./components/layouts/Footer";
import Container from "./components/containers/Container";
import ScrollToTop from "./components/ScrollToTop";
import Seo from "./components/Seo";

import ContainerBgImage from "./assets/images/main-bg.webp";
import "./App.css";

// ✅ Context
import { LanguageProvider } from "./context/LanguageContext";
import { AppProviders } from "./AppProviders";


// ✅ Sahifalarni lazy tarzda yuklaymiz
const Home = lazy(() => import("./pages/home/Home"));
const ErrorPage = lazy(() => import("./pages/error/ErrorPage"));
const ProductDetailPage = lazy(() => import("./pages/product/ProductDetail"));
const Profile = lazy(() => import("./pages/profile/Profile"));
const Search = lazy(() => import("./pages/search/Search"));
const MobilCategoryPage = lazy(() => import("./pages/menu/MobilCategoryPage"));
const Basket = lazy(() => import("./pages/menu/Basket"));
const Favorites = lazy(() => import("./pages/menu/Favorites"));
const Category = lazy(() => import("./pages/categories/Category"));
const CommentsPage = lazy(() => import("./components/comment/CommentsPage"));
const OrdersConfirmation = lazy(
  () => import("./pages/orders/OrdersConfirmation")
);
const OrderSuccess = lazy(() => import("./pages/orders/OrderSuccess"));
const Questions = lazy(() => import("./pages/home/Questions"));
const About = lazy(() => import("./pages/menu/About"));
const TopProducts = lazy(() => import("./pages/product/TopProducts"));
const Store = lazy(() => import("./pages/menu/Store"));
const Stores = lazy(() => import("./pages/stores/Stores"));
const Agreement = lazy(() => import("./pages/home/Agreement"));
const DeliveryTrackingPage = lazy(() => import("./pages/delivery/DeliveryTrackingPage").then(module => ({ default: module.DeliveryTrackingPage })));

const App: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  const [initialized, setInitialized] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // URL-dan tilni aniqlash
  const pathParts = location.pathname.split("/");
  const potentialLang = pathParts[1];

  useEffect(() => {
    const validLanguages = ["ru", "uz"];

    if (validLanguages.includes(potentialLang)) {
      // Til mavjud bo'lsa, uni o'zgartiramiz
      i18n.changeLanguage(potentialLang);
    } else if (location.pathname === "/") {
      // Root uchun redirect qilmasdan default tilni o'rnatamiz
      const defaultLang = "uz";
      i18n.changeLanguage(defaultLang);
    } else {
      // Boshqa sahifalar uchun til yo'q bo'lsa, qo'shamiz va redirect qilamiz
      const defaultLang = "uz";
      i18n.changeLanguage(defaultLang);
      const newPath = `/${defaultLang}${location.pathname}`;
      navigate(newPath, { replace: true });
      return;
    }

    setInitialized(true);
  }, [location.pathname, i18n, navigate]);

  // Mobil o‘lchamni kuzatish
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Sahifa turlari
  const isProductPage = location.pathname.includes("/product/");
  const isProfilePage = location.pathname.includes("/profile");
  const isBasketPage = location.pathname.includes("/basket");
  const isFavoritesPage = location.pathname.includes("/favorites");
  const isHome = location.pathname.includes("/");

  // Footer sharti
  const showFooter = isMobile
    ? !(isProfilePage || isBasketPage || isFavoritesPage || isHome)
    : true;

  // Navbar sharti
  // const showNavbar = isMobile
  //   ? !(isProductPage)
  //   : true;

  // Background image sharti: Category sahifasi uchun (Home va MobilCategoryPage'da o'chirildi)
  const showBackgroundImage = location.pathname.startsWith(
    `/${potentialLang}/category/`
  );

  if (!initialized) return null;

  const isAboutPage = location.pathname.includes("/about");

  return (
    <Suspense fallback={<div className="min-h-screen">Loading...</div>}>
      <AppProviders>
        <LanguageProvider lang={potentialLang || "uz"}>
          <Seo key={location.pathname} />
          <div className="min-h-screen flex flex-col">
            {/* Navbar */}
            {<Navbar isProfilePage={isProfilePage} isMobile={isMobile} />}
            {/* Full-screen background wrapper */}
            <div className="relative min-h-screen w-full overflow-x-hidden">
              {showBackgroundImage && (
                <img
                  src={ContainerBgImage}
                  alt="Background" 
                  loading="eager"
                  decoding="sync"
                  className="absolute top-0 left-0 w-full h-full object-cover -z-10"
                />
              )}

              {/* Container for content with max-width constraint */}
              {isAboutPage || isProductPage ? (
                <main className="flex-grow">
                  <Routes>
                    <Route path="/:lang/about" element={<About />} />
                    <Route
                      path="/:lang/product/:id"
                      element={<ProductDetailPage />}
                    />
                  </Routes>
                </main>
              ) : (
                <Container
                  style={{
                    backgroundImage: ContainerBgImage,
                    minHeight: showBackgroundImage ? "100vh" : "auto",
                  }}
                >
                  <main className="flex-grow">
                    <Toaster
                      position="top-center"
                      toastOptions={{
                        duration: 3000,
                      }}
                    />
                    <Routes>
                      <Route path="/" element={<Home />} />
                      <Route path="/:lang">
                        <Route index element={<Home />} />
                        <Route path="profile/*" element={<Profile />} />
                        <Route path="search" element={<Search />} />
                        <Route path="basket" element={<Basket />} />
                        <Route path="favorites" element={<Favorites />} />
                        <Route
                          path="mobile-category"
                          element={<MobilCategoryPage />}
                        />
                        <Route path="category/:id" element={<Category />} />
                        <Route
                          path="orders-confirmation"
                          element={<OrdersConfirmation />}
                        />
                        <Route
                          path="orders-success"
                          element={<OrderSuccess />}
                        />
                        <Route
                          path="tracking/:id"
                          element={<DeliveryTrackingPage />}
                        />

                        <Route path="top-product" element={<TopProducts />} />
                        <Route path="comments/:id" element={<CommentsPage />} />
                        <Route path="questions" element={<Questions />} />
                        <Route path="agreement" element={<Agreement />} />
                        {/* <Route path="about" element={<About />} /> */}

                        <Route
                          path="store/:store_address"
                          element={<Store />}
                        />
                        <Route path="stores" element={<Stores />} />
                        {/* error pages */}
                        <Route
                          path="server-error"
                          element={<ErrorPage type="500" />}
                        />
                        <Route
                          path="forbidden"
                          element={<ErrorPage type="403" />}
                        />
                        <Route
                          path="network-error"
                          element={<ErrorPage type="network" />}
                        />
                        <Route path="*" element={<ErrorPage type="404" />} />
                      </Route>
                    </Routes>
                  </main>
                </Container>
              )}
            </div>

            {showFooter && <Footer />}
          </div>
          <ScrollToTop />
        </LanguageProvider>
      </AppProviders>
    </Suspense>
  );
};
export default App;

